import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { unwrapPaginated } from "@/config/api";
import { uploadAccept, uploadHint, validateUploadFile } from "@/config/uploads";
import { formatDate } from "@/i18n/format";
import { getApiErrorMessage } from "@/lib/apiError";
import type { Article, ArticleCreateUpdateRequest, Company } from "@/services/moderators/generated/model";
import {
  useModeratorsArticlesCreate,
  useModeratorsArticlesDestroy,
  useModeratorsArticlesList,
  useModeratorsArticlesUpdate,
} from "@/services/moderators/generated/moderators-articles/moderators-articles";
import { useModeratorsCompaniesList } from "@/services/moderators/generated/moderators-companies/moderators-companies";
import "./ModeratorPage.css";
import "./ModeratorArticlesPage.css";

type CategoryKey = "REGULATION" | "GEOPOLITICS" | "BUSINESS_NEWS";

const CATEGORIES: readonly CategoryKey[] = ["REGULATION", "GEOPOLITICS", "BUSINESS_NEWS"];

const isCategory = (value: string): value is CategoryKey => (CATEGORIES as readonly string[]).includes(value);

interface FormState {
  id: number | null;
  title: string;
  category: CategoryKey;
  brief: string;
  source: string;
  releaseDate: string;
  fullArticleLink: string;
  targetCompanyIds: number[];
  pdfFile: File | null;
}

const emptyForm: FormState = {
  id: null,
  title: "",
  category: "REGULATION",
  brief: "",
  source: "",
  releaseDate: "",
  fullArticleLink: "",
  targetCompanyIds: [],
  pdfFile: null,
};

const ModeratorArticlesPage = () => (
  <ModeratorLayout>
    <ArticlesContent />
  </ModeratorLayout>
);

const ArticlesContent = () => {
  const { t } = useTranslation(["moderator", "common"]);
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<CategoryKey | "">("");
  const [form, setForm] = useState<FormState>(emptyForm);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const queryParams = useMemo(() => {
    const params: { category?: string } = {};
    if (categoryFilter) params.category = categoryFilter;
    return params;
  }, [categoryFilter]);

  const { data, isLoading, error } = useModeratorsArticlesList(queryParams);
  const articles = unwrapPaginated<Article>(data);

  // Companies for the target picker. We pull a single page and rely on the
  // moderator panel's existing search semantics — a moderator targeting a
  // long tail of companies should use the search box rather than scroll.
  const { data: companiesData } = useModeratorsCompaniesList(undefined);
  const companies = unwrapPaginated<Company>(companiesData);

  const createMutation = useModeratorsArticlesCreate();
  const updateMutation = useModeratorsArticlesUpdate();
  const deleteMutation = useModeratorsArticlesDestroy();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return articles;
    return articles.filter((a) => a.title.toLowerCase().includes(q) || (a.source || "").toLowerCase().includes(q));
  }, [articles, search]);

  const refetchAll = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/v1/moderators/articles/"] });
  };

  const openCreate = () => {
    setForm(emptyForm);
    setFormError(null);
    setIsModalOpen(true);
  };

  const openEdit = (a: Article) => {
    setForm({
      id: a.id,
      title: a.title,
      category: (a.category as CategoryKey) || "REGULATION",
      brief: a.brief || "",
      source: a.source || "",
      releaseDate: a.releaseDate ? a.releaseDate.slice(0, 10) : "",
      fullArticleLink: a.fullArticleLink || "",
      // Article read serializer doesn't expose target_companies — clear by
      // default and let the moderator re-target on edit if needed.
      targetCompanyIds: [],
      pdfFile: null,
    });
    setFormError(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setForm(emptyForm);
    setFormError(null);
  };

  const submitForm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setFormError(t("articles.titleRequired"));
      return;
    }
    if (form.pdfFile) {
      const invalid = validateUploadFile(form.pdfFile, "pdf");
      if (invalid) {
        setFormError(invalid);
        return;
      }
    }

    // The PDF file requires multipart. Build FormData when a file is attached
    // and let axios infer the boundary; otherwise send plain JSON for cleaner
    // typed payloads.
    const hasFile = form.pdfFile != null;
    let payload: ArticleCreateUpdateRequest | FormData;
    if (hasFile) {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("category", form.category);
      if (form.brief) fd.append("brief", form.brief);
      if (form.source) fd.append("source", form.source);
      if (form.releaseDate) fd.append("releaseDate", form.releaseDate);
      if (form.fullArticleLink) fd.append("fullArticleLink", form.fullArticleLink);
      for (const id of form.targetCompanyIds) fd.append("targetCompanyIds", String(id));
      if (form.pdfFile) fd.append("pdfFile", form.pdfFile);
      payload = fd as unknown as ArticleCreateUpdateRequest;
    } else {
      payload = {
        title: form.title.trim(),
        category: form.category,
        brief: form.brief || null,
        source: form.source || null,
        releaseDate: form.releaseDate || null,
        fullArticleLink: form.fullArticleLink || null,
        targetCompanyIds: form.targetCompanyIds,
      };
    }

    const opts = {
      onSuccess: () => {
        refetchAll();
        closeModal();
      },
      onError: (err: unknown) => setFormError(getApiErrorMessage(err, t("articles.saveError"))),
    };

    if (form.id != null) {
      updateMutation.mutate({ id: form.id, data: payload as ArticleCreateUpdateRequest }, opts);
    } else {
      createMutation.mutate({ data: payload as ArticleCreateUpdateRequest }, opts);
    }
  };

  const confirmDelete = (id: number) => {
    deleteMutation.mutate(
      { id },
      {
        onSuccess: () => {
          refetchAll();
          setDeletingId(null);
        },
        onError: () => setDeletingId(null),
      },
    );
  };

  return (
    <div>
      <header className="mod-page__header">
        <div>
          <h1 className="mod-page__title">{t("articles.title")}</h1>
          <p className="mod-page__subtitle">{t("articles.subtitle")}</p>
        </div>
        <button type="button" className="mod-page__btn mod-page__btn--primary" onClick={openCreate}>
          <i className="fa-solid fa-plus" /> {t("articles.new")}
        </button>
      </header>

      <div className="mod-page__toolbar">
        <input
          type="search"
          className="mod-page__input"
          placeholder={t("articles.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="mod-page__select"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as CategoryKey | "")}
        >
          <option value="">{t("articles.allCategories")}</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {t(`articles.categories.${c}`)}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? <div className="mod-page__loading">{t("articles.loading")}</div> : null}
      {error ? <div className="mod-page__error">{t("articles.loadError")}</div> : null}
      {!isLoading && filtered.length === 0 ? <div className="mod-page__empty">{t("articles.empty")}</div> : null}

      <div className="mod-articles__list">
        {filtered.map((a) => (
          <article key={a.id} className="mod-page__card mod-articles__card">
            <div className="mod-page__card-body">
              <div className="mod-articles__card-header">
                <span className={`mod-page__chip mod-articles__chip--${a.category}`}>
                  {isCategory(a.category) ? t(`articles.categories.${a.category}`) : a.category.replace(/_/g, " ")}
                </span>
                <span
                  className={`mod-page__chip ${a.isBroadcast ? "mod-page__chip--success" : "mod-page__chip--warning"}`}
                >
                  {a.isBroadcast ? t("articles.broadcast") : t("articles.targeted")}
                </span>
              </div>
              <h2 className="mod-page__card-title">{a.title}</h2>
              {a.brief ? <p className="mod-articles__brief">{a.brief}</p> : null}
              <p className="mod-page__card-meta">
                {a.source ? <span>{t("articles.sourceMeta", { source: a.source })}</span> : null}
                {a.releaseDate ? (
                  <span>{t("articles.released", { date: formatDate(a.releaseDate.slice(0, 10)) })}</span>
                ) : null}
                {a.fullArticleLink ? (
                  <a href={a.fullArticleLink} target="_blank" rel="noopener noreferrer">
                    {t("articles.originalLink")}
                  </a>
                ) : null}
                {a.pdfFileUrl ? (
                  <a href={a.pdfFileUrl} target="_blank" rel="noopener noreferrer">
                    <i className="fa-regular fa-file-pdf" /> {t("articles.pdf")}
                  </a>
                ) : null}
              </p>
            </div>
            <div className="mod-articles__actions">
              <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={() => openEdit(a)}>
                {t("common:actions.edit")}
              </button>
              <button type="button" className="mod-page__btn mod-page__btn--danger" onClick={() => setDeletingId(a.id)}>
                {t("common:actions.delete")}
              </button>
            </div>
          </article>
        ))}
      </div>

      {isModalOpen && (
        <div className="mod-modal__overlay" onClick={closeModal}>
          <div className="mod-modal__content" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <header className="mod-modal__header">
              <h2 className="mod-modal__title">{form.id != null ? t("articles.edit") : t("articles.new")}</h2>
              <button
                type="button"
                className="mod-modal__close"
                onClick={closeModal}
                aria-label={t("common:actions.close")}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </header>

            <form onSubmit={submitForm} className="mod-modal__form">
              <label className="mod-modal__label">
                {t("articles.form.title")}
                <input
                  type="text"
                  className="mod-page__input"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                />
              </label>

              <div className="mod-modal__row">
                <label className="mod-modal__label">
                  {t("articles.form.category")}
                  <select
                    className="mod-page__select"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value as CategoryKey })}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {t(`articles.categories.${c}` as "articles.categories.REGULATION")}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="mod-modal__label">
                  {t("articles.form.releaseDate")}
                  <input
                    type="date"
                    className="mod-page__input"
                    value={form.releaseDate}
                    onChange={(e) => setForm({ ...form, releaseDate: e.target.value })}
                  />
                </label>
              </div>

              <label className="mod-modal__label">
                {t("articles.form.brief")}
                <textarea
                  className="mod-page__input"
                  rows={3}
                  value={form.brief}
                  onChange={(e) => setForm({ ...form, brief: e.target.value })}
                />
              </label>

              <div className="mod-modal__row">
                <label className="mod-modal__label">
                  {t("articles.form.source")}
                  <input
                    type="text"
                    className="mod-page__input"
                    value={form.source}
                    onChange={(e) => setForm({ ...form, source: e.target.value })}
                  />
                </label>
                <label className="mod-modal__label">
                  {t("articles.originalLink")}
                  <input
                    type="url"
                    className="mod-page__input"
                    value={form.fullArticleLink}
                    onChange={(e) => setForm({ ...form, fullArticleLink: e.target.value })}
                  />
                </label>
              </div>

              <label className="mod-modal__label">
                {t("articles.form.pdf")}
                <input
                  type="file"
                  accept={uploadAccept("pdf")}
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    const invalid = file ? validateUploadFile(file, "pdf") : null;
                    setFormError(invalid);
                    if (invalid) e.target.value = "";
                    setForm({ ...form, pdfFile: invalid ? null : file });
                  }}
                />
                <small>{t("articles.form.pdfHint", { hint: uploadHint("pdf") })}</small>
              </label>

              <label className="mod-modal__label">
                {t("articles.form.targetCompanies")}
                <select
                  multiple
                  size={Math.min(8, Math.max(4, companies.length))}
                  className="mod-page__select"
                  value={form.targetCompanyIds.map(String)}
                  onChange={(e) => {
                    const ids = Array.from(e.target.selectedOptions, (o) => Number(o.value));
                    setForm({ ...form, targetCompanyIds: ids });
                  }}
                >
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.companyName}
                    </option>
                  ))}
                </select>
              </label>

              {formError ? <p className="mod-modal__error">{formError}</p> : null}

              <div className="mod-modal__footer">
                <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={closeModal}>
                  {t("common:actions.cancel")}
                </button>
                <button
                  type="submit"
                  className="mod-page__btn mod-page__btn--primary"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? t("common:actions.saving")
                    : t("common:actions.save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deletingId != null && (
        <div className="mod-modal__overlay" onClick={() => setDeletingId(null)}>
          <div className="mod-modal__content mod-modal__content--small" onClick={(e) => e.stopPropagation()}>
            <h2 className="mod-modal__title">{t("articles.deleteTitle")}</h2>
            <p>{t("articles.deleteBody")}</p>
            <div className="mod-modal__footer">
              <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={() => setDeletingId(null)}>
                {t("common:actions.cancel")}
              </button>
              <button
                type="button"
                className="mod-page__btn mod-page__btn--danger"
                onClick={() => confirmDelete(deletingId)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? t("common:actions.deleting") : t("common:actions.delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModeratorArticlesPage;
